import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { h } from "preact";
import { ApiBackedComponent } from "../../common/components/ApiBackedComponent";
import BarChart from "../../common/components/BarChart";
import { MakeGenericApiCall } from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";
import { NbiDimensions } from "../../common/generic/generic-nbi-dimensions";
import { IGenericRequest } from "../../common/generic/generic-types";
import {
  BuildPortfolioFilters,
  GetPortfolioFilterAsofDate,
} from "../../common/portfolio/portfolio-filter";
import { IProps } from "../../common/portfolio/portfolio-types";
import { mapData } from "../utils/helper";
import { getCommonPptExport } from "../../common/export/chart-helper";
import { find } from "lodash";
import { maxSelectedClients } from "../../common/utils/clientlistHelper";

const kpis = "nbi_ytd,nbi_ytd_1,nbi_ytd_2";
const attributes = "fpv_bl";
const sortOrder = "fpv_bl";

interface IState {
  data: any[];
  symbolLabel: string;
  xLabels: string[];
}

export class NbiPerBLAndYearClass extends ApiBackedComponent<
  IProps & IWidgetConfigurationProps,
  IState
> {
  protected child: BarChart;
  public constructor() {
    super();
    this.setState({
      data: [],
      symbolLabel: "K",
      xLabels: [],
    });
  }

  public getPptExport() {
    if (!this.child) {
      return undefined;
    }

    return getCommonPptExport("bar", this.child.getChartOptions());
  }

  protected renderWithDataReady(): JSX.Element {
    const yAxis = [
      {
        format: "{value}",
        labels: {
          format: "{value} " + this.state.symbolLabel,
        },
        title: {
          text: "NBI (" + this.state.symbolLabel + "€)",
        },
      },
    ];
    const modifiedColors = ["#6488ff", "#b4bbcb", "#444852"];
    return (
      <BarChart
        ref={instance => (this.child = instance)}
        data={this.state.data}
        symbolLabel={this.state.symbolLabel}
        xLabels={this.state.xLabels}
        yAxis={yAxis}
        colors={modifiedColors}
      />
    );
  }

  protected async fetchApiData(props: IProps) {
    this.initCorrelationId();
    const attr = new NbiDimensions();
    const headers = props.headers;
    const filters = BuildPortfolioFilters(props.filters);
    const isClientMandatory =
      props.attribute === attr.CLIENT_GEN_ID &&
      filters.filter(x => x.attribute === attr.CLIENT_GEN_ID).length === 0;
    if (isClientMandatory) {
      this.setState({
        data: [],
        xLabels: [],
      });
      this.setDataStatus("no-client");
      return;
    }
    this.setDataStatus("loading");

    const dateFilter = GetPortfolioFilterAsofDate(props.filters);
    const genericRequest: IGenericRequest = {
      aggregationAttributes: attributes,
      date: dateFilter,
      filter: GenericFilterBuilder(filters),
      kpis,
      sort: sortOrder,
    };

    const filterClientsSelected = find(filters, {
      attribute: "clientgen_id",
    });
    const clientsCount = filterClientsSelected
      ? filterClientsSelected.value.split("+").length
      : 0;
    if (filterClientsSelected && clientsCount >= maxSelectedClients) {
      this.setState({
        data: [],
      });
      this.setDataStatus("too-many-clients");
    } else {
      const responseData = await MakeGenericApiCall(
        headers,
        "nbi",
        genericRequest,
        this.props.widgetConfiguration,
        this.state.correlationId
      );
      const errorStatus = CheckReponseDataForError(responseData);
      if (errorStatus) {
        this.setDataStatus(errorStatus);
        this.setStatusText(responseData.statusText);
        this.setState({
          data: [],
          xLabels: [],
        });
      } else {
        const result = mapData(responseData);
        if (result.data.length !== 0) {
          this.setState({
            data: result.data,
            symbolLabel: result.symbollabel,
            xLabels: result.xLabels,
          });
          this.setDataStatus("ready");
        } else {
          this.setState({
            data: [],
            xLabels: [],
          });
          this.setDataStatus("no-data");
        }
      }
    }
  }
}
export const NbiPerBLAndYear = withWidgetConfiguration(NbiPerBLAndYearClass);
